import { clientMiddleware, useUrlNormalization } from '@hendrx/core/common';
import { NotFoundMessage } from '@hendrx/core/common/components/messages/PageMessage/Messages/NotFoundMessage';
import { DndProvider } from '@hendrx/core/common/components/providers/DndProvider';
import { createRootRoute, HeadContent, Outlet } from '@tanstack/react-router';

import { useGoogleTagManager } from 'shared/client/hooks/useGoogleTagManager';
import { getUserData } from 'shared/queries/authenticationApi';
import { getEnvVar } from 'shared/utils/getEnvVar';

import AuthProvider from '@/app/_providers/AuthProvider';
import { GlobalErrorTracker } from '@/app/_providers/GlobalErrorTracker';
import { GlobalStylesProvider } from '@/app/_providers/GlobalStylesProvider';
import { RouteChangeTracker } from '@/app/_providers/RouteChangeTracker';
import TranslationsProvider from '@/app/_providers/TranslationsProvider';
import UIProvider from '@/app/_providers/UIProvider';
import XApiProvider from '@/app/_providers/XApiProvider';
import { getQueryClient } from '@/app/getQueryClient';

function NotFound() {
  return <NotFoundMessage />;
}

function RootComponent() {
  useUrlNormalization();
  useGoogleTagManager(getEnvVar('VITE_GTM_TAG', { throwOnMissing: false }));

  return (
    <>
      <HeadContent />
      <TranslationsProvider>
        <UIProvider>
          <GlobalStylesProvider>
            <XApiProvider>
              <AuthProvider>
                <DndProvider>
                  <GlobalErrorTracker />
                  <RouteChangeTracker />
                  <Outlet />
                </DndProvider>
              </AuthProvider>
            </XApiProvider>
          </GlobalStylesProvider>
        </UIProvider>
      </TranslationsProvider>
    </>
  );
}

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    const user = await getQueryClient().ensureQueryData({
      queryKey: ['userData'],
      queryFn: getUserData
    });
    const auth = {
      isUser: !!user,
      isAdmin: user?.role === 'admin' ? true : user ? false : null
    };

    const searchString =
      typeof location.search === 'string'
        ? location.search
        : location.search
        ? new URLSearchParams(location.search as Record<string, string>).toString()
        : '';

    clientMiddleware({
      auth,
      location: {
        pathname: location.pathname,
        search: searchString
      },
      search: (typeof location.search === 'object' ? location.search : {}) as Record<string, unknown>
    });

    return { auth };
  },
  head: () => {
    return {
      meta: [],
      links: [
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: '/favicon-16x16.png'
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: '/favicon-32x32.png'
        }
      ]
    };
  },
  component: RootComponent,
  notFoundComponent: NotFound
});
